import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PersonAddIcon from "@material-ui/icons/PersonAdd";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";
import UserStatusIcon from "../../components/UserModal/statusIcon";

import moment from "moment";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext, SocketManager } from "../../context/Socket/SocketContext";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { profileI18nKey } from "../../helpers/userAccess";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";



const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "USER_PRESENCE") {
    const { id, online, lastPresenceAt } = action.payload;
    const uid = Number(id);
    const userIndex = state.findIndex((u) => Number(u.id) === uid);
    if (userIndex === -1) {
      return state;
    }
    const next = [...state];
    next[userIndex] = {
      ...next[userIndex],
      online,
      ...(lastPresenceAt !== undefined && lastPresenceAt !== null
        ? { lastPresenceAt }
        : {})
    };
    return next;
  }

  if (action.type === "MERGE_ONLINE_FROM_SERVER") {
    const fresh = action.payload;
    if (!Array.isArray(fresh) || fresh.length === 0) return state;
    const map = new Map(fresh.map((u) => [Number(u.id), u]));
    return state.map((u) => {
      const f = map.get(Number(u.id));
      if (!f) return u;
      return {
        ...u,
        online: Boolean(f.online),
        ...(f.lastPresenceAt !== undefined && f.lastPresenceAt !== null
          ? { lastPresenceAt: f.lastPresenceAt }
          : {}),
      };
    });
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: 0,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: 16,
    boxShadow: theme.mode === 'dark'
      ? '0 10px 28px rgba(0, 0, 0, 0.28)'
      : '0 14px 34px rgba(37, 99, 235, 0.10)',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#334155' : '#dbeafe'}`,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    "& .MuiTextField-root": {
      minWidth: 260,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: 0,
      },
    },
  },
  departmentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    justifyContent: 'center',
    maxWidth: '200px',
    alignItems: 'flex-start',
    margin: '0 auto',
    width: '100%',
    maxHeight: '60px',
    overflow: 'hidden',
    position: 'relative',
  },
  departmentChip: {
    color: 'white',
    padding: '4px 8px',
    borderRadius: 999,
    fontSize: '10px',
    fontWeight: 800,
    maxWidth: '100%',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: '1.2',
    textAlign: 'center',
    display: 'inline-block',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
    margin: '1px',
  },
  departmentOverflow: {
    position: 'absolute',
    bottom: '0',
    right: '0',
    backgroundColor: theme.mode === 'dark' ? '#6b7280' : '#666',
    color: 'white',
    padding: '2px 4px',
    borderRadius: '8px',
    fontSize: '9px',
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    minWidth: 34,
    width: 34,
    height: 34,
    borderRadius: 12,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#dbe3ef"}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#ffffff",
    color: theme.palette.text.secondary,
    transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.mode === "dark" ? "rgba(96, 165, 250, 0.12)" : "#eff6ff",
      borderColor: theme.palette.primary.main,
    },
  },
  editButton: {
    color: theme.palette.primary.main,
  },
  deleteButton: {
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: theme.mode === "dark" ? "rgba(220, 38, 38, 0.14)" : "#fef2f2",
    },
  },
  addButton: {
    borderRadius: 999,
    padding: '8px 16px',
    fontWeight: 700,
    textTransform: "none",
  },
  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#f8fafc',
    fontWeight: 800,
    fontSize: '12px',
    color: theme.mode === 'dark' ? '#e5e7eb' : '#334155',
    borderBottom: `1px solid ${theme.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#263244' : '#f8fafc',
    },
    transition: 'background-color 0.2s ease',
    '& td': {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
    },
  },
  profileBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  userName: {
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  mutedText: {
    color: theme.palette.text.secondary,
    fontSize: "0.84rem",
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
  },
  lastPresenceOnline: {
    color: '#059669',
    fontWeight: 800,
    fontSize: '13px',
  },
  adminBadge: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '1px solid #a7f3d0',
  },
  supervisorBadge: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fde68a',
  },
  userBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    border: '1px solid #bfdbfe',
  },
}));

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const [viewMode, setViewMode] = useViewMode("users");

  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const handleUserSocket = useCallback(
    (data) => {
      if (!data) return;
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    },
    []
  );

  const handleUserPresence = useCallback(
    (payload) => {
      if (!payload || payload.id == null) return;
      const mine =
        user?.companyId != null && user?.companyId !== ""
          ? Number(user.companyId)
          : Number(localStorage.getItem("companyId"));
      if (
        payload.companyId != null &&
        Number.isFinite(mine) &&
        Number(payload.companyId) !== mine
      ) {
        return;
      }
      dispatch({
        type: "USER_PRESENCE",
        payload: {
          id: payload.id,
          online: Boolean(payload.online),
          lastPresenceAt: payload.lastPresenceAt
        }
      });
    },
    [user?.companyId]
  );

  useEffect(() => {
    const companyId =
      user?.companyId != null && user?.companyId !== ""
        ? String(user.companyId)
        : localStorage.getItem("companyId");
    if (companyId == null || companyId === "") {
      return undefined;
    }

    const socket = socketManager.getSocket(companyId);
    const evUser = `company-${companyId}-user`;

    socket.off(evUser, handleUserSocket);
    socket.on(evUser, handleUserSocket);
    // Evento fijo (backend libs/socket emitUserPresence) — misma cadena en todos los clientes
    socket.off("user-presence", handleUserPresence);
    socket.on("user-presence", handleUserPresence);

    return () => {
      if (socket && typeof socket.off === "function") {
        socket.off(evUser, handleUserSocket);
        socket.off("user-presence", handleUserPresence);
      }
    };
  }, [socketManager, user?.companyId, handleUserSocket, handleUserPresence]);

  /** M6: tras reconexión engine.io, volver a leer online/lastPresence desde API (evita UI “pegada” en offline) */
  useEffect(() => {
    const companyId =
      user?.companyId != null && user?.companyId !== ""
        ? String(user.companyId)
        : localStorage.getItem("companyId");
    if (companyId == null || companyId === "") {
      return undefined;
    }
    const syncPresence = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber: 1 },
        });
        dispatch({ type: "MERGE_ONLINE_FROM_SERVER", payload: data.users });
      } catch (_) {
        /* evitar toast en background */
      }
    };
    return SocketManager.subscribeConnect(syncPresence);
  }, [searchParam, user?.companyId]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      <MainHeader>
        <Title subtitle="Gestiona los miembros del equipo, sus perfiles de acceso e invitaciones.">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {i18n.t("users.title")}
            <GuideOpenButton id="invite" title={i18n.t("users.title")} to="/users" startId="add" />
          </span>
        </Title>
        <MainHeaderButtonsWrapper className={classes.headerActions}>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Can
            role={user.profile}
            perform="user-modal:editProfile"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
                className={classes.addButton}
                startIcon={<PersonAddIcon />}
              >
                {i18n.t("users.buttons.add")}
              </Button>
            )}
          />
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {users.map((teamUser) => (
              <RecordCard
                key={`user-card-${teamUser.id}`}
                title={teamUser.name}
                actions={
                  <div className={classes.actionButtons}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(teamUser)}
                      className={`${classes.actionButton} ${classes.editButton}`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (teamUser.id === 1) {
                          toast.error("❌ No se puede eliminar el Super Usuario Master (ID: 1). Esta es la cuenta de configuración por defecto del sistema.");
                          return;
                        }
                        setConfirmModalOpen(true);
                        setDeletingUser(teamUser);
                      }}
                      className={`${classes.actionButton} ${classes.deleteButton}`}
                      disabled={teamUser.id === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </div>
                }
              >
                <RecordCardMeta>{teamUser.email}</RecordCardMeta>
                <RecordCardMeta>
                  {teamUser.online
                    ? i18n.t("users.table.connected")
                    : i18n.t("users.table.disconnected")}
                  {" · "}
                  {i18n.t(profileI18nKey(teamUser.profile))}
                </RecordCardMeta>
                <RecordCardMeta>
                  {teamUser.queues && teamUser.queues.length > 0
                    ? teamUser.queues.map((queue) => queue.name).join(", ")
                    : "Sin departamento"}
                </RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
			<TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.id")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>{i18n.t("users.table.status")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>{i18n.t("users.table.name")}</TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.email")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.profile")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader} style={{ width: '200px', minWidth: '200px' }}>
                {i18n.t("users.table.department")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.lastConnection")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("users.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {users.map((user) => (
                <TableRow key={`user-row-${user.id}`} className={classes.tableRow} hover>
				  <TableCell align="center">{user.id}</TableCell>
				  <TableCell align="center">
                    <div className={classes.statusCell}>
                      <UserStatusIcon user={user} />
                      <span className={classes.mutedText}>
                        {user.online
                          ? i18n.t("users.table.connected")
                          : i18n.t("users.table.disconnected")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align="center"><span className={classes.userName}>{user.name}</span></TableCell>
                  <TableCell align="center"><span className={classes.mutedText}>{user.email}</span></TableCell>
                  <TableCell align="center">
                    <span className={`${classes.profileBadge} ${
                      user.profile === 'admin' ? classes.adminBadge : user.profile === 'supervisor' ? classes.supervisorBadge : classes.userBadge
                    }`}>
                      {i18n.t(profileI18nKey(user.profile))}
                    </span>
                  </TableCell>
                  <TableCell align="center" style={{ width: '200px', minWidth: '200px' }}>
                    {user.queues && user.queues.length > 0 ? (
                      <div className={classes.departmentContainer}>
                        {user.queues.slice(0, 3).map((queue, index) => (
                          <span
                            key={`user-${user.id}-queue-${queue.id || 'unknown'}-${index}`}
                            className={classes.departmentChip}
                            style={{
                              backgroundColor: queue.color || '#1976d2',
                            }}
                          >
                            {queue.name}
                          </span>
                        ))}
                        {user.queues.length > 3 && (
                          <span className={classes.departmentOverflow}>
                            +{user.queues.length - 3} más
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className={classes.mutedText}>Sin departamento</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {user.online ? (
                      <span className={classes.lastPresenceOnline}>
                        {i18n.t("users.table.lastConnectionNow")}
                      </span>
                    ) : user.lastPresenceAt ? (
                      <span className={classes.mutedText}>{moment(user.lastPresenceAt).format("DD/MM/YYYY HH:mm:ss")}</span>
                    ) : (
                      <span className={classes.mutedText}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.actionButtons}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        className={`${classes.actionButton} ${classes.editButton}`}
                        title="Editar usuario"
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          if (user.id === 1) {
                            toast.error("❌ No se puede eliminar el Super Usuario Master (ID: 1). Esta es la cuenta de configuración por defecto del sistema.");
                            return;
                          }
                          setConfirmModalOpen(true);
                          setDeletingUser(user);
                        }}
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        title={user.id === 1 ? "No se puede eliminar el Super Usuario Master" : "Eliminar usuario"}
                        disabled={user.id === 1}
                        style={{
                          opacity: user.id === 1 ? 0.5 : 1,
                          cursor: user.id === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton key="loading-skeleton" columns={8} />}
            </>
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Users;
